export type FbeCharacteristicsValueType = string | boolean | number;

export type FbeCharacteristics = {
  characteristicName: string;
  characteristicValue: FbeCharacteristicsValueType;
  operator?: string;
};

export type FbeCriteriaCharacteristics = {
  and?: FbeCharacteristics[];
  or?: FbeCharacteristics[];
};

export type FbeCriteria = {
  characteristics?: FbeCriteriaCharacteristics;
  networkCharacteristics?: FbeCriteriaCharacteristics;
};

export type FbeOption = {
  value: string;
  label: string;
  key: string;
  disabled: boolean;
}


export enum InjectTime {
  GENERATION = 'generation',
}

export enum ExternalDataType {
  DYNAMO_DB = 'dynamoDB',
}

export enum ExternalDataSystem {
  IES = 'ies',
}

export enum ExternalDataTable {
  CHARACTERISTICS = 'characteristics',
  NETWORK = 'network',
}

export enum MappingOperator {
  EQUALS = 'equals',
  INCLUDES = 'includes',
  NOT_EQUALS = 'notEquals',
}

export enum MappingType {
  BOOLEAN = 'boolean',
  NUMBER = 'number',
  STRING = 'string',
}

export type ExternalDataMapping = {
  source: string;
  dest: string;
  value?: FbeCharacteristicsValueType;
  type: MappingType;
  operator?: MappingOperator;
  compareValue?: string;
  default?: string;
  missingData?: boolean;
  isFormLevelUsage?: boolean;
};

export type ExternalData = {
  injectTime?: InjectTime; // set by API
  mapping: ExternalDataMapping[];
  system?: ExternalDataSystem; // set by API
  type?: ExternalDataType; // set by API
  table?: ExternalDataTable; // set by API
};

export enum FbeSourceDefinitionFormat {
  IES = 'ies',
}

export enum FbeGenerationType {
  AUTO = 'auto',
}

export type LocalisedText = {
  text: string;
  language: string;
};

export enum FbeRepeatType {
  DIRECT = 'once',
  DAILY = 'daily',
  WEEKLY = 'weekly',
}

export enum TacoRole {
  INSTALLER = 'installer',
  NEB_SUPERVISOR = 'nebSupervisor',
  TESTER = 'tester',
  SEB_SUPERVISOR = 'sebSupervisor',
  SEB_SERVICE_ENGINEER = 'sebServiceEngineer',
  SUBCONTRACTOR_COMPANY = 'subcontractorCompany',
  SUBCONTRACTOR_PERSON_IN_CHARGE = 'subcontractorPersonInCharge',
  SUBCONTRACTOR_INSTALLER = 'subcontractorInstaller',
  SUBCONTRACTOR_TESTER = 'subcontractorTester',
}

export type FbeResultEmailDestination = string | TacoRole;

export enum FbeResultDefinitionFormat {
  INTERNAL = 'internal',
  JSON = 'json',
  PDF = 'pdf',
}

export enum FbeResultSystemDestination {
  DATABASE = 'database',
  SHAREPOINT_LIST = 'sharepointList',
  TRACE_PDM = 'tracePdm',
  NONE = 'none',
}

export enum FbeSourceDestination {
  IES_FRM = 'iesFrm',
}

export enum FbeType {
  ADD_IF_NOT_EXISTS = 'addifnotexists',
  INDEPENDENT = 'independent',
  FULL_MERGE = 'fullmerge',
  MERGE = 'merge',
  REPLACE = 'replace',
}

export enum FbeWorkflowStatus {
  TO_BE_STARTED = 'toBeStarted',
  FOR_INSTALLER_ACCEPTANCE = 'forInstallerAcceptance',
  NONE = '',
}

export enum FbeResultWorkflowStatus {
  FOR_TESTER_ACCEPTANCE = 'forTesterAcceptance',
  FOR_SEB_ACCEPTANCE = 'forSebAcceptance',
  SEB_ACCEPTED = 'sebAccepted',
  FOR_FINAL_INSPECTION = 'forFinalInspection',
  INSTALLATION_COMPLETE = 'installationComplete',
  NONE = '',
}

export enum FbeSectionFeature {
  NO_LOCAL_QUESTION = 'noLocalQuestions',
  ALLOW_REMOVALS_BY_SPV= 'allowRemovalsBySPV',
  ALLOW_REORDERING_BY_SPV= 'allowReorderingBySPV',
}

export enum FbeFormFeature {
  ALLOW_CHANGE_ROLE = 'allowChangeRole',
  ALLOW_ADDITION = 'allowAddition',
  ALLOW_REMOVALS_BY_SPV = 'allowRemovalsBySPV',
  ALLOW_REORDERING_BY_SPV = 'allowReorderingBySPV',
  NO_LOCAL_SECTIONS = 'noLocalSections',
  MANDATORY = 'mandatory',
  MUST_ANSWER = 'mustAnswer',
  NO_OPEN_DEVIATIONS = 'noOpenDeviations',
  NONE = 'none',
  SIGNOFF = 'signoff',
  SITE_INFORMATION = 'siteInformation',
  SKIP_QUESTIONS = 'skipQuestions',
  SUMMARY = 'summary',
}

export enum FbeWorkflowStates {
  TO_BE_STARTED = 'toBeStarted',
  FOR_INSTALLER_ACCEPTANCE = 'forInstallerAcceptance',
  INSTALLER_ACCEPTED = 'installerAccepted',
  FOR_INSTALLATION_INSPECTION = 'forInstallationInspection',
  FOR_TESTER_ACCEPTANCE = 'forTesterAcceptance',
  TESTER_ACCEPTED = 'testerAccepted',
  FOR_FINAL_INSPECTION = 'forFinalInspection',
  FOR_SEB_ACCEPTANCE = 'forSebAcceptance',
  SEB_ACCEPTED = 'sebAccepted',
  SEB_REJECTED = 'sebRejected',
  INSTALLATION_COMPLETE = 'installationComplete',
  INSTALLATION_ARCHIVED = 'installationArchived',
}

export type FbeResultHandlingType = {
  resultEmailReceivers: FbeResultEmailDestination[];
  resultFormat: FbeResultDefinitionFormat[];
  resultSystemDestination: FbeResultSystemDestination[];
  resultWorkflowStatus: FbeResultWorkflowStatus[];
}

export enum FbeMode {
  LIVE = 'live',
  TEST_QDPLUS = 'testqdplus',
  TEST_NEBSEB = 'testnebseb',
}

export const fbeFormFields = [
  'pk',
  'sk',
  'active',
  'activeWorkflowStatus',
  'appliedCountries',
  'createdAt',
  'createdBy',
  'createdByName',
  'criteria',
  'description',
  'defaultLanguage',
  'descriptions',
  'externalData',
  'features',
  'format',
  'generationType',
  'lockedFields',
  'maxPoints',
  'mode',
  'modifiedAt',
  'modifiedBy',
  'modifiedByName',
  'name',
  'formId',
  'order',
  'pass',
  'passingScorePercentage',
  'questionSetIdRange',
  'repeatType',
  'resultHandling',
  'role',
  'sourceDestination',
  'templateVersion',
  'workflowTask',
  'totalPoints',
  'type',
  'version',
  'workflowStatus',
  'languages',
];

export type FbeForm = {
  pk?: string; // not for post, only get and put
  sk?: string; // not for post, only get and put
  active: boolean;
  activeWorkflowStatus: string[];
  appliedCountries: string[];
  createdAt?: string; // automatically set
  createdBy?: string; // automatically set
  createdByName?: string; // automatically set
  criteria: FbeCriteria;
  defaultLanguage?: string;
  description?: string;
  descriptions?: LocalisedText[];
  externalData?: ExternalData[];
  features: FbeFormFeature[];
  format?: FbeSourceDefinitionFormat; // set by API
  generationType?: FbeGenerationType; // set by API
  lockedFields?: string[];
  maxPoints?: number;
  mode: FbeMode;
  modifiedAt: string; // automatically set
  modifiedBy: string; // automatically set
  modifiedByName?: string; // automatically set
  name: LocalisedText[];
  formId: string;
  order: number;
  pass?: boolean; // result value, automatically set
  passingScorePercentage?: number;
  questionSetIdRange?: string; // set by API
  repeatType: FbeRepeatType;
  resultHandling: FbeResultHandlingType[];
  role: TacoRole[];
  sourceDestination?: FbeSourceDestination; // set by API
  templateVersion: string;
  workflowTask?: string;
  taskType?: string; // legacy read compatibility
  totalPoints?: number;
  type: FbeType;
  version?: number; // set by API
  workflowStatus: FbeWorkflowStatus;
  languages?: string[];
};

export type FbeLoop = {
  name: LocalisedText[];
  ascending: boolean;
  total: number;
  externalVariable?: string;
  externalVariables?: string[];
  ascendingInternalVariable?: string;
  ascendingInternalValue?: string;
};

export enum FbeDependencyType {
  NONE = 'none',
  QUESTION = 'question',
  VARIABLE = 'variable',
}

export enum TableType {
  NUMBER = 'number',
  PLUMBING = 'plumbing'
}

export enum LogicalOperator {
  AND = 'and',
  OR = 'or'
}

export enum DependencyOperator {
  EQUALS = 'equals',
  NOT_EQUALS = 'notEquals',
  INCLUDES = 'includes',
  NOT_INCLUDES = 'notIncludes',
  GREATER = 'greater',
  SMALLER = 'smaller',
}

export type FbeDependency = {
  logicalOperator: LogicalOperator;
  variable: string;
  operator: DependencyOperator;
  compareVariable?: string;
  compareValue?: string;
}

export type FbeTask = {
  task: LocalisedText[];
  taskId: string;
  parentTaskId: string;
  order: number;
  total: number;
  timeInMinutes: number;
  externalDependency?: FbeDependency[];
  internalDependency?: FbeDependency[];
};

export enum FbeQuestionType {
  DATE = 'date',
  DEADLINE = 'deadline',
  INFO = 'info',
  FREE = 'free',
  NUMBER = 'number',
  NUMBER_CORRECTION = 'numberCorrection',
  NUMBER_HIDDEN = 'numberHidden',
  NUMBER_WITHOUT_DEVIATION = 'numberWithoutDeviation',
  NUMBER_TABLE = 'numberTable',
  PICTURE = 'picture',
  PICTURE_NA = 'pictureNa',
  CHOICE = 'choice',
  MULTI_SELECT = 'multiSelect',
  SELECT = 'select',
  SELECT_ONO = 'selectOkNotOk',
  SELECT_ONONA = 'selectOkNotOkNa',
  SELECT_WARNING_ONONA = 'selectOkNotOkWarningNa',
  SELECT_YN = 'selectYesNo',
  SELECT_YN_CHOICE = 'selectYesNoChoice',
  SELECT_YNNAE = 'selectYesNoNaExisting',
  SELECT_YES_NA_EX_WITHOUT_DEVIATION = 'selectYesNaExistingWithoutDeviation',
  SET_INTERNAL_VARIABLE = 'setInternalVariable',
  SIGNATURE = 'signature',
  TEXT = 'string',
  TEXT_WITHOUT_DEVIATION = 'stringWithoutDeviation',
  HEADER = 'header',
  WARNING = 'warning',
  SAFETY_YES = 'safetyYes',
  SAFETY_YES_NO = 'safetyYesNo',
}

export enum FbeQuestionAnswerAction {
  CREATE_DEVIATION = 'createDeviation',
}

export type FbeQuestionAnswer = {
  answer: LocalisedText[];
  action?: FbeQuestionAnswerAction;
  score: number;
};

export enum FbeReferenceType {
  TEXT = 'text',
  IMAGE = 'image',
  TIP_IMAGE = 'tipimage',
}

export type FbeReference = {
  type: FbeReferenceType;
  anchor?: string[];
  location?: string;
};

export enum FbeVariableType {
  BOOLEAN = 'boolean',
  NUMBER = 'number',
  STRING = 'string',
}

export type FbeVariableTranslations = {
  key: string;
  value: string | number | boolean;
  type?: FbeVariableType
};

export enum FbeQuestionFeature {
  CREATE_DEVIATION = 'createDeviation',
  EXTEND_STATIC_QUESTION = 'staticQuestion',
  MANIPULATE_IN_TABLE = 'manipulateInTable',
  MUST_ANSWER = 'mustAnswer',
  UNIQUE = 'unique',
  ALLOW_REMOVALS_BY_SPV= 'allowRemovalsBySPV',
  ALLOW_REORDERING_BY_SPV= 'allowReorderingBySPV',
}

export enum FbeQuestionLoopRepeatType {
  AFTER = 'after',
  BEFORE = 'before',
  NONE = 'none',
  REPEAT = 'repeat',
}

export enum FbeNumberMultiAdjustmentDirectionType {
  NONE = 'none',
  X = 'X',
  Y = 'Y',
}

export enum FbeNumberMultiAdjustmentImpactType {
  MINUS = 'minus',
  NONE = 'none',
  PLUS = 'plus',
}

export type FbeRowPositionName = {
  position: number;
  name: LocalisedText[];
}

export const fbeQuestionFields = [
 'actualValue',
  'additionalInfo',
  'alternativeQuestionType',
  'answer',
  'answers',
  'columnShort',
  'externalDependency',
  'internalDependency',
  'externalVariable',
  'features',
  'groupId',
  'internalOutputVariable',
  'internalVariable',
  'isGlobal',
  'labels',
  'lockedFields',
  'loopRepeat',
  'maxAllowedValue',
  'maxAllowedExternalVariable',
  'maxAllowedInternalVariable',
  'minAllowedValue',
  'minAllowedExternalVariable',
  'minAllowedInternalVariable',
  'multiAdjustmentDirection',
  'multiAdjustmentImpact',
  'mustScore',
  'order',
  'pattern',
  'question',
  'questionId',
  'questionShort',
  'questionType',
  'references',
  'rowShort',
  'timeIndication',
  'toleranceMinValue',
  'toleranceMinExternalVariable',
  'toleranceMinInternalVariable',
  'tolerancePlusValue',
  'tolerancePlusExternalVariable',
  'tolerancePlusInternalVariable',
  'variableTranslations',
  'actualValueInternalVariable',
  'weight',
  'ascendingOrientation',
  'ascendingOrientationInternalVariable',
  'ascendingOrientationInternalValue',
  'tableType',
  'rowPositionNames',
];

export type FbeQuestion = {
  actualValue?: string;
  additionalInfo?: LocalisedText[];
  alternativeQuestionType?: FbeQuestionType;
  answer?: string;
  answers?: FbeQuestionAnswer[];
  columnShort?: LocalisedText[];
  externalDependency?: FbeDependency[];
  internalDependency?: FbeDependency[];
  externalVariable?: string;
  features?: FbeQuestionFeature[];
  groupId?: string;
  internalOutputVariable?: string;
  internalVariable?: string;
  isGlobal?: boolean;
  labels?: string[];
  lockedFields?: string[];
  loopRepeat?: FbeQuestionLoopRepeatType;
  maxAllowedValue?: number;
  maxAllowedExternalVariable?: string;
  maxAllowedInternalVariable?: string;
  minAllowedValue?: number;
  minAllowedExternalVariable?: string;
  minAllowedInternalVariable?: string;
  multiAdjustmentDirection?: FbeNumberMultiAdjustmentDirectionType;
  multiAdjustmentImpact?: FbeNumberMultiAdjustmentImpactType;
  mustScore?: boolean;
  order: number;
  pattern?: string;
  question: LocalisedText[];
  questionId?: string; // Set by API
  questionShort?: LocalisedText[];
  questionType: FbeQuestionType;
  references?: FbeReference[];
  rowShort?: LocalisedText[];
  timeIndication?: string;
  toleranceMinValue?: number;
  toleranceMinExternalVariable?: string;
  toleranceMinInternalVariable?: string;
  tolerancePlusValue?: number;
  tolerancePlusExternalVariable?: string;
  tolerancePlusInternalVariable?: string;
  variableTranslations?: FbeVariableTranslations[];
  actualValueInternalVariable?: string;
  weight?: number;
  ascendingOrientation?: boolean;
  ascendingOrientationInternalVariable?: string;
  ascendingOrientationInternalValue?: string;
  tableType?: TableType;
  rowPositionNames?: FbeRowPositionName[];
};

export type IesMergeMetaData = {
  questionSetId: string; // provided by method
  parentSetId: string; // provided by method
  tacoRole: string[];
  description: LocalisedText[];
  tag: string;
};

export const fbeSectionFields = [
  'pk',
  'sk',
  'active',
  'description',
  'displayType',
  'externalDependency',
  'features',
  'groupId',
  'id',
  'internalCode',
  'internalDependency',
  'lockedFields',
  'loop',
  'maxPoints',
  'mergeMetaData',
  'modifiedAt',
  'modifiedBy',
  'modifiedByName',
  'name',
  'order',
  'pass',
  'passingScorePercentage',
  'questions',
  'references',
  'tasks',
  'workflowTask',
  'totalPoints',
  'workflowDependency',
];

export type FbeSection = {
  pk?: string; // Set by API
  sk?: string; // Set by API
  active: boolean;
  description?: string;
  descriptions?: LocalisedText[];
  displayType?: FbeSectionDisplayType; // Set by API
  externalDependency?: FbeDependency[];
  features?: FbeSectionFeature[];
  groupId?: string;
  groupHeader?: boolean;
  id?: number;
  internalCode?: string; // Set by API
  internalDependency?: FbeDependency[];
  lockedFields?: string[];
  loop?: FbeLoop;
  maxPoints?: number; // result value, automatically set
  mergeMetaData?: IesMergeMetaData;
  modifiedAt?: string; // Set by API
  modifiedBy?: string; // Set by API
  modifiedByName?: string; // Set by API
  name: LocalisedText[];
  order: number;
  pass?: boolean; // result value, automatically set
  passingScorePercentage?: number;
  questions: FbeQuestion[];
  references?: FbeReference[];
  tasks: FbeTask[];
  workflowTask?: string;
  totalPoints?: number; // result value, automatically set
  workflowDependency?: string[];
};

export type FbeFrontlineInfo = {
  countries: string[];
  setId: string;
  languages: string[];
}

export type FbeFrontlineSettings = {
  pk: string;
  sk: string;
  countries: {
    [key: string]: string;
  },
  countryDisplayNames: {
    [key: string]: string;
  },
  languageDisplayNames: {
    [key: string]: string;
  },
} & {
  [key: string]: string | object | FbeFrontlineInfo;
};

export type FbeFrontline = {
  settings: FbeFrontlineSettings;
  frontline: string;
};

export enum FbeSectionDisplayType {
  LIST = 'list',
  SINGLE = 'single',
  TABLE = 'table',
  SINGLE_LIST = 'singleList',
}

export const getSectionDisplayTypeName = (
  type: string
): string => {
  switch(type){
    case FbeSectionDisplayType.LIST:
      return 'List';
    case FbeSectionDisplayType.SINGLE:
      return 'Single element';
    case FbeSectionDisplayType.TABLE:
      return 'Table';
      case FbeSectionDisplayType.SINGLE_LIST:
      return 'Single List';
    default:
      return type;
  }
}

export const getSectionFeatureDisplayName = (
  feature: string
): string => {
  switch(feature){
    case FbeSectionFeature.NO_LOCAL_QUESTION:
      return 'No local questions allowed (extend)';
      case FbeSectionFeature.ALLOW_REMOVALS_BY_SPV:
      return 'Allow removals by SPV';
      case FbeSectionFeature.ALLOW_REORDERING_BY_SPV:
      return 'Allow reordering by SPV';
    default:
      return feature;
  }
}

export const getFormFeatureDisplayName = (
  feature: string
): string => {
  switch(feature){
    case FbeFormFeature.ALLOW_CHANGE_ROLE:
      return 'Allow role changes by SPV';
    case FbeFormFeature.ALLOW_ADDITION:
      return 'Allow additions by SPV';
    case FbeFormFeature.ALLOW_REMOVALS_BY_SPV:
      return 'Allow removals by SPV';
       case FbeFormFeature.ALLOW_REORDERING_BY_SPV:
      return 'Allow reordering by SPV';
    case FbeFormFeature.NO_LOCAL_SECTIONS:
      return 'No local sections allowed (extend)';
    case FbeFormFeature.MANDATORY:
      return 'Is mandatory';
    case FbeFormFeature.MUST_ANSWER:
      return 'Must answer';
    case FbeFormFeature.NO_OPEN_DEVIATIONS:
      return 'No open deviations';
    case FbeFormFeature.NONE:
      return 'None';
    case FbeFormFeature.SIGNOFF:
      return 'Signoff';
    case FbeFormFeature.SITE_INFORMATION:
      return 'Site information';
    case FbeFormFeature.SKIP_QUESTIONS:
      return 'Allow to skip questions';
    case FbeFormFeature.SUMMARY:
      return 'Display summary';
    default:
      return feature;
  }

}

export const getWorkflowDisplayName = (
  workflowState: string
): string => {
  switch(workflowState){
    case FbeWorkflowStates.TO_BE_STARTED:
      return 'To be started';
    case FbeWorkflowStates.FOR_INSTALLER_ACCEPTANCE:
      return 'For installer acceptance';
    case FbeWorkflowStates.INSTALLER_ACCEPTED:
      return 'Installer accepted';
    case FbeWorkflowStates.FOR_INSTALLATION_INSPECTION:
      return 'For installation inspection';
    case FbeWorkflowStates.FOR_TESTER_ACCEPTANCE:
      return 'For tester acceptance';
    case FbeWorkflowStates.TESTER_ACCEPTED:
      return 'Tester accepted';
    case FbeWorkflowStates.FOR_FINAL_INSPECTION:
      return 'For final inspection';
    case FbeWorkflowStates.FOR_SEB_ACCEPTANCE:
      return 'For service acceptance';
    case FbeWorkflowStates.SEB_ACCEPTED:
      return 'Service accepted';
    case FbeWorkflowStates.SEB_REJECTED:
      return 'Service rejected';
    case FbeWorkflowStates.INSTALLATION_COMPLETE:
      return 'Installation complete';
    case FbeWorkflowStates.INSTALLATION_ARCHIVED:
      return 'Installation archived';
    default:
      return workflowState;
  }
}

export const getRoleDisplayName = (
  role: string
): string => {
  switch(role){
    case TacoRole.INSTALLER:
      return 'Installer';
    case TacoRole.NEB_SUPERVISOR:
      return 'Field Supervisor';
    case TacoRole.TESTER:
      return 'Tester';
    case TacoRole.SEB_SUPERVISOR:
      return 'Service Supervisor';
    case TacoRole.SEB_SERVICE_ENGINEER:
      return 'Service Technician';
    case TacoRole.SUBCONTRACTOR_COMPANY:
      return 'Subcontractor Company';
    case TacoRole.SUBCONTRACTOR_PERSON_IN_CHARGE:
      return 'Subcontractor Person In Charge';
    case TacoRole.SUBCONTRACTOR_INSTALLER:
      return 'Subcontractor Installer';
    case TacoRole.SUBCONTRACTOR_TESTER:
      return 'Subcontractor Tester';
    default:
      return role;
  }
}

export const getFormTypeDisplayName = (
  type: string
): string => {
  switch(type){
    case FbeType.ADD_IF_NOT_EXISTS:
      return 'Add if not exists';
    case FbeType.INDEPENDENT:
      return 'Independent';
    case FbeType.FULL_MERGE:
      return 'Full merge';
    case FbeType.MERGE:
      return 'Merge (after)';
    case FbeType.REPLACE:
      return 'Replace';
    default:
      return type;
  }
}

export const getRepeatTypeDisplayName = (
  type: string
): string => {
  switch(type){
    case FbeRepeatType.DIRECT:
      return 'One time';
    case FbeRepeatType.DAILY:
      return 'Recurring daily';
    case FbeRepeatType.WEEKLY:
      return 'Recurring weekly';
    default:
      return type;
  }
}

export const getMappingTypeDisplayName = (
  type: string
): string => {
  switch(type){
    case MappingType.BOOLEAN:
      return 'Boolean (true or false)';
    case MappingType.NUMBER:
      return 'Number';
    case MappingType.STRING:
      return 'String';
    default:
      return type;
  }
}

export const getMappingTypeOperatorDisplayName = (
  type: string
): string => {
  switch(type){
    case MappingOperator.EQUALS:
      return 'Equals';
    case MappingOperator.INCLUDES:
      return 'Includes';
    case MappingOperator.NOT_EQUALS:
      return 'Different';
    case '':
      return 'No operator';
    default:
      return type;
  }
}

export const getQuestionTypeDisplayName = (
  type: string
): string => {
  switch(type){
    case FbeQuestionType.DATE:
      return 'Date';
    case FbeQuestionType.DEADLINE:
      return 'Deadline';
    case FbeQuestionType.INFO:
      return 'Info';
    case FbeQuestionType.FREE:
      return 'Free text';
    case FbeQuestionType.NUMBER:
      return 'Number (scoring?)';
    case FbeQuestionType.NUMBER_CORRECTION:
      return 'Number (correction)';
    case FbeQuestionType.NUMBER_HIDDEN:
      return 'Number (hidden)';
    case FbeQuestionType.NUMBER_WITHOUT_DEVIATION:
      return 'Number (without deviation)';
    case FbeQuestionType.NUMBER_TABLE:
      return 'Number table';
    case FbeQuestionType.PICTURE:
      return 'Picture';
    case FbeQuestionType.PICTURE_NA:
      return 'Picture | Not Applicable';
    case FbeQuestionType.CHOICE:
      return 'Route';
    case FbeQuestionType.MULTI_SELECT:
      return 'Multi select (scoring?)';
    case FbeQuestionType.SELECT:
      return 'Select (scoring?)';
    case FbeQuestionType.SELECT_ONO:
      return 'Select OK | Not OK (deviation, scoring)';
    case FbeQuestionType.SELECT_ONONA:
      return 'Select OK | Not OK | Not Applicable (deviation, scoring)';
    case FbeQuestionType.SELECT_WARNING_ONONA:
      return 'Select OK | Not OK | Not Applicable (warning, deviation, scoring)';
    case FbeQuestionType.SELECT_YN:
      return 'Select Yes | No (deviation, scoring)';
    case FbeQuestionType.SELECT_YN_CHOICE:
      return 'Select Yes | No';
    case FbeQuestionType.SELECT_YNNAE:
      return 'Select Yes | No | Not available | Existing (deviation, scoring)';
    case FbeQuestionType.SELECT_YES_NA_EX_WITHOUT_DEVIATION:
      return 'Select Yes | Not available | Existing (without deviation)';
    case FbeQuestionType.SET_INTERNAL_VARIABLE:
      return 'Set internal variable';
    case FbeQuestionType.SIGNATURE:
      return 'Signature';
    case FbeQuestionType.TEXT:
      return 'String';
    case FbeQuestionType.TEXT_WITHOUT_DEVIATION:
      return 'String (without deviation)';
    case FbeQuestionType.HEADER:
      return 'Header';
    case FbeQuestionType.WARNING:
      return 'Warning';
    case FbeQuestionType.SAFETY_YES:
      return 'Safety Yes (deviation, scoring)';
    case FbeQuestionType.SAFETY_YES_NO:
      return 'Safety Yes | No (deviation, scoring)';
    default:
      return type;
  }
}

export const getQuestionFeatureDisplayName = (
  feature: string
): string => {
  switch(feature){
    case FbeQuestionFeature.CREATE_DEVIATION:
      return 'Create deviation';
    case FbeQuestionFeature.EXTEND_STATIC_QUESTION:
      return 'Mandatory question (extend)';
    case FbeQuestionFeature.MANIPULATE_IN_TABLE:
      return 'Manipulate in table';
    case FbeQuestionFeature.MUST_ANSWER:
      return 'Must answer';
    case FbeQuestionFeature.UNIQUE:
      return 'Unique';
      case FbeQuestionFeature.ALLOW_REMOVALS_BY_SPV:
      return 'Allow removals by SPV';
      case FbeQuestionFeature.ALLOW_REORDERING_BY_SPV:
      return 'Allow reordering by SPV';
    default:
      return feature;
  }
}

export const getQuestionLoopRepeatDisplayName = (
  type: string
): string => {
  switch(type){
    case FbeQuestionLoopRepeatType.AFTER:
      return 'After';
    case FbeQuestionLoopRepeatType.BEFORE:
      return 'Before';
    case FbeQuestionLoopRepeatType.REPEAT:
      return 'Repeat';
    default:
      return type;
  }
}

export const getAnswerActionDisplayName = (
  action: string
): string => {
  switch(action){
    case FbeQuestionAnswerAction.CREATE_DEVIATION:
      return 'Create deviation';
    default:
      return action;
  }
}

export const getMultiAdjustmentDirectionDisplayName = (
  direction: string
): string => {
  switch(direction){
    case FbeNumberMultiAdjustmentDirectionType.X:
      return 'Horizontal (X)';
    case FbeNumberMultiAdjustmentDirectionType.Y:
      return 'Vertical (Y)';
    default:
      return direction;
  }
}

export const getMultiAdjustmentImpactDisplayName = (
  impact: string
): string => {
  switch(impact){
    case FbeNumberMultiAdjustmentImpactType.MINUS:
      return 'Opposite (-)';
    case FbeNumberMultiAdjustmentImpactType.PLUS:
      return 'Follow direction (+)';
    default:
      return impact;
  }
}

export const getResultSystemDestinationDisplayName = (
  destination: string
): string => {
  switch(destination){
    case FbeResultSystemDestination.DATABASE:
      return 'Analytic database';
    case FbeResultSystemDestination.SHAREPOINT_LIST:
      return 'Sharepoint list';
    case FbeResultSystemDestination.TRACE_PDM:
      return 'Trace PDM';
    case FbeResultSystemDestination.NONE:
      return 'None';
    default:
      return destination;
  }
}

export const getLogicalOperatorDisplayName = (
  operator: string
): string => {
  switch(operator){
    case LogicalOperator.AND:
      return 'AND';
    case LogicalOperator.OR:
      return 'OR';
    default:
      return operator;
  }
}

export const getTableTypeDisplayName = (
  tableType: string
): string => {
  switch(tableType){
    case TableType.NUMBER:
      return 'Regular';
    case TableType.PLUMBING:
      return 'Plumbing';
    default:
      return tableType;
  }
}

export const getModeDisplayName = (
  mode: string
): string => {
  switch(mode){
    case FbeMode.LIVE:
      return 'Live';
    case FbeMode.TEST_QDPLUS:
      return 'Installer (qdplus)';
    case FbeMode.TEST_NEBSEB:
      return 'Tester (nebseb)';
    default:
      return mode;
  }
}


export const getOperatorDisplayName = (
  operator: string
): string => {
  switch(operator){
    case DependencyOperator.EQUALS:
      return 'Equals';
    case DependencyOperator.NOT_EQUALS:
      return 'Not equals';
    case DependencyOperator.INCLUDES:
      return 'Includes';
    case DependencyOperator.NOT_INCLUDES:
      return 'Not includes';
    case DependencyOperator.SMALLER:
      return 'Smaller';
    case DependencyOperator.GREATER:
      return 'Greater';
    default:
      return operator;
  }
}

export const getCountryDisplayName = (
  countryIsoCode: string,
  countryConfig: { [key: string]: string },
): string => {
  const code = countryIsoCode.toLowerCase();

  if(countryConfig[code]){
    return countryConfig[code];
  }

  switch(code.toLowerCase()){
    case 'uk':
      return 'United Kingdom';
    case 'gb':
      return 'Great Britain';
    case 'ir':
      return 'Ireland';
    case 'nl':
      return 'Netherlands';

    case 'sp':
      return 'Spain';
    case 'pt':
      return 'Portugal';
    case 'it':
      return 'Italy';
    case 'mc':
      return 'Monaco';
    case 'ad':
      return 'Andorra';

    case 'se':
      return 'Sweden';
    case 'no':
      return 'Norway';
    case 'dk':
      return 'Denmark';
    case 'fi':
      return 'Finland';
    case 'ee':
      return 'Estonia';
    case 'lt':
      return 'Lithuania';
    case 'lv':
      return 'Latvia';

    case 'de':
      return 'Germany';
    case 'at':
      return 'Austria';
    case 'ch':
      return 'Switzerland';

    case 'be':
      return 'Belgium';
    case 'fr':
      return 'France';
    case 'lu':
      return 'Luxembourg';

    case 'pl':
      return 'Poland';
    case 'ua':
      return 'Ukraine';
    case 'ro':
      return 'Romania';
    case 'cz':
      return 'Czechia';
    case 'sk':
      return 'Slovakia';
    case 'rs':
      return 'Serbia';
    case 'me':
      return 'Montenegro';
    case 'hr':
      return 'Croatia';
    case 'hu':
      return 'Hungary';
    case 'bg':
      return 'Bulgaria';
    case 'gr':
      return 'Greece';
    case 'mk':
      return 'North Macedonia';
    case 'al':
      return 'Albaina';
    case 'il':
      return 'Israel';
    case 'ba':
      return ' Bosnia and Herzegovina';
    case 'cy':
      return 'Cyprus';

    case 'tr':
      return 'Turkey';

    case 'global':
      return 'Global';

    default:
      return `Country ${countryIsoCode}`;
  }
}

const languageNames: Record<string, string> = {
  en: 'English',
  nl: 'Dutch',
  se: 'Swedish',
  dk: 'Danish',
  fi: 'Finnish',
  no: 'Norwegian',
  ee: 'Estonian',
  lt: 'Lithuanian',
  lv: 'Latvian',
  de: 'German',
  at: 'Austrian German',
  ch: 'Swiss German',
  fr: 'French',
  be: 'Belgian',
  lu: 'Luxembourgish',
  ad: 'Andorran Catalan',
  it: 'Italian',
  mc: 'Monégasque',
  pl: 'Polish',
  ua: 'Ukrainian',
  ro: 'Romanian',
  cz: 'Czech',
  sk: 'Slovak',
  rs: 'Serbian',
  me: 'Montenegrin',
  hr: 'Croatian',
  hu: 'Hungarian',
  bg: 'Bulgarian',
  gr: 'Greek',
  mk: 'Macedonian',
  al: 'Albanian',
  il: 'Hebrew',
  ba: 'Bosnian',
  cy: 'Greek/Turkish (Cyprus)',
  tr: 'Turkish',
  ar: 'Arabic',
  bh: 'Bahraini Arabic',
  qa: 'Qatari Arabic',
  kz: 'Kazakh',
  in: 'Hindi',
  vn: 'Vietnamese',
  ph: 'Filipino',
  my: 'Malay',
  th: 'Thai',
  id: 'Indonesian',
  es: 'Spanish',
};

export const getLanguageDisplayName = (
  languageIsoCode: string,
  languageConfig: { [key: string]: string },
): string => {
  const code = languageIsoCode.toLowerCase();

  if(languageConfig[code]){
    return languageConfig[code];
  }

  return languageNames[code] || `Language (${code})`;
}
